import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";

// ── Property Helpers for Estimates ──
const getEstimateKey = (item) => item?.qbId || item?.TxnID || item?.raw?.TxnID || item?._id;

const getEstimateDocNumber = (item) =>
  item?.docNumber || item?.RefNumber || item?.raw?.RefNumber || "—";

const getEstimateCustomerName = (item) =>
  item?.customerName ||
  item?.CustomerRef?.FullName ||
  item?.raw?.CustomerRef?.FullName ||
  "—";

const getEstimateDate = (item) =>
  item?.txnDate || item?.TxnDate || item?.raw?.TxnDate || item?.createdAt;

const getEstimateTotal = (item) =>
  item?.totalAmount ??
  item?.subtotal ??
  item?.customer_subtotal ??
  item?.Subtotal ??
  item?.TotalAmount ??
  item?.raw?.TotalAmount ??
  item?.raw?.Subtotal ??
  0;

const getEstimateTotalTax = (item) =>
  Number(
    item?.totalTax ??
    item?.total_tax ??
    item?.raw?.SalesTaxTotal ??
    item?.taxDetails?.totalTax ??
    0
  );

const getEstimateEmail = (item) =>
  item?.email ||
  item?.BillEmail?.Address ||
  item?.raw?.BillEmail?.Address ||
  item?.sendBy?.email ||
  item?.senderEmail ||
  "—";

const getEstimateSource = (item) =>
  item?.source === "quickbookEstimate" || item?.type === "quickbookEstimate"
    ? "QuickBooks"
    : "QB Desktop";

const getEstimateStatus = (item) => {
  if (item?.status) {
    const s = String(item.status).toLowerCase();
    if (s === "pending" || s === "open") return "Open";
    if (s === "closed") return "Closed";
    if (s === "accepted") return "Accepted";
    if (s === "rejected") return "Rejected";
    return item.status.charAt(0).toUpperCase() + item.status.slice(1);
  }
  if (item?.IsFullyInvoiced) return "Closed";
  if (item?.IsActive === false) return "Closed";
  return "Open";
};

const getEstimateIsInvoiced = (item) => {
  const s = String(item?.status || "").toLowerCase();
  return s === "closed" || s === "accepted" || !!item?.IsFullyInvoiced;
};

const getEstimateLines = (item) =>
  item?.originalLineItems?.length
    ? item.originalLineItems
    : item?.markupLineItems?.length
      ? item.markupLineItems
      : item?.lines?.length
        ? item.lines
        : item?.EstimateLineRet?.length
          ? item.EstimateLineRet
          : item?.Line?.length
            ? item.Line
            : item?.raw?.EstimateLineRet?.length
              ? item.raw.EstimateLineRet
              : item?.raw?.Line?.length
                ? item.raw.Line
                : [];

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const StatusBadge = ({ status }) => {
  const map = {
    Open: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
    Closed: { bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-200", dot: "bg-gray-400" },
    Accepted: { bg: "bg-[#F0FEF4]", text: "text-[#008236]", border: "border-[#0a7822a6]", dot: "bg-[#0a7822a6]" },
    Rejected: { bg: "bg-red-50", text: "text-red-600", border: "border-red-200", dot: "bg-red-500" },
  };
  const c = map[status] || { bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-200", dot: "bg-gray-400" };
  return (
    <span className={`inline-flex capitalize items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
      <span className={`size-1.5 rounded-full ${c.dot}`} />
      {status || "—"}
    </span>
  );
};

const StatCard = ({ label, value, bg, icon }) => (
  <div className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4">
    <div className={`size-12 rounded-xl ${bg} flex items-center justify-center shrink-0`}>{icon}</div>
    <div>
      <p className="text-sm text-gray-600 font-medium">{label}</p>
      <p className="text-xl font-semibold text-gray-800 mt-0.5">{value}</p>
    </div>
  </div>
);

export default function QBDEstimateList() {
  const history = useNavigate();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [estimates, setEstimates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "open" | "accepted" | "closed" | "rejected"
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchEstimates = async () => {
    loader(true);
    await ApiClient.get(`qb-desktop/list/estimates?companyId=${companyId}`)
      .then((res) => { if (res?.success) setEstimates(res?.data || []); })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => { fetchEstimates(); }, []);

  const STATUS_TABS = [
    { label: "All", value: "" },
    { label: "Open", value: "open" },
    { label: "Accepted", value: "accepted" },
    { label: "Closed", value: "closed" },
    { label: "Rejected", value: "rejected" },
  ];

  const getTabCount = (tabVal) => {
    if (!tabVal) return estimates.length;
    return estimates.filter((e) => getEstimateStatus(e).toLowerCase() === tabVal).length;
  };

  const filteredEstimates = estimates.filter((e) => {
    const q = searchQuery.toLowerCase();
    const customer = getEstimateCustomerName(e).toLowerCase();
    const docNum = String(getEstimateDocNumber(e)).toLowerCase();
    const qbId = String(getEstimateKey(e)).toLowerCase();
    const matchesSearch = !q || customer.includes(q) || docNum.includes(q) || qbId.includes(q);

    const st = getEstimateStatus(e).toLowerCase();
    const matchesStatus = !statusFilter ? true : st === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalAmt = estimates.reduce((acc, e) => acc + getEstimateTotal(e), 0);

  const closeModal = () => { setIsViewModalOpen(false); setSelectedEstimate(null); };

  const handleConvertToInvoice = (estimate) => {
    const lines = getEstimateLines(estimate).map((l) => ({
      itemListId: l.itemListId || l.ItemListID || l.ItemRef?.ListID || l.raw?.ItemRef?.ListID || "",
      description: l.description || l.Desc || l.Description || l.raw?.Desc || "",
      quantity: Number(l.quantity || l.Quantity || l.Qty || 1),
      rate: Number(l.rate || l.Rate || l.UnitPrice || l.Amount || 0),
    }));

    history("/qb-desktop/invoices/add", {
      state: {
        estimate: {
          customerListId: estimate.raw?.CustomerRef?.ListID || estimate.CustomerRef?.ListID || estimate.customerListId || estimate.CustomerListID || "",
          txnId: getEstimateKey(estimate),
          refNumber: getEstimateDocNumber(estimate) !== "—" ? getEstimateDocNumber(estimate) : "",
          lines,
        },
      },
    });
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">QB Desktop – Estimates</h2>
            <p className="text-sm text-gray-600">{estimates.length} estimate{estimates.length !== 1 ? "s" : ""}</p>
          </div>
          <button
            type="button"
            onClick={() => history("/qb-desktop/estimates/add")}
            className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" /><path d="M12 5v14" />
            </svg>
            Create Estimate
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Total Estimates"
            value={estimates.length}
            bg="bg-[#115B9D17]"
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#115B9D]"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>}
          />
          <StatCard
            label="Total Amount"
            value={formatCurrency(totalAmt)}
            bg="bg-[#f0fdf4]"
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#008236]"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>}
          />
          <StatCard
            label="Open Estimates"
            value={estimates.filter((e) => getEstimateStatus(e).toLowerCase() === "open").length}
            bg="bg-amber-50"
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>}
          />
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* Filters & Search */}
          <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
            {/* <div className="max-sm:w-full w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
              {STATUS_TABS.map((tab) => {
                const count = getTabCount(tab.value);
                const isActive = statusFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isActive
                        ? "bg-white text-gray-900 shadow-sm font-semibold"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        isActive
                          ? "bg-[#115B9D]/10 text-[#115B9D] font-bold"
                          : "bg-gray-200/70 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div> */}
            <div className="relative w-full sm:w-[320px]">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by customer, ref number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Actions", "Doc #", "Customer", "Txn Date", "Status", "Source", "Total Tax", "Email", "Total Amount"].map((h) => (
                    <th key={h} className="text-left py-3 px-3">
                      <span className="text-xs text-gray-700 font-medium block">{h}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEstimates.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">No estimates found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredEstimates.map((item, index) => {
                    const estKey = getEstimateKey(item) || index;
                    const docNum = getEstimateDocNumber(item);
                    const custName = getEstimateCustomerName(item);
                    const estDate = getEstimateDate(item);
                    const amount = getEstimateTotal(item);
                    const statusText = getEstimateStatus(item);
                    const totalTax = getEstimateTotalTax(item);
                    const email = getEstimateEmail(item);
                    const sourceText = getEstimateSource(item);
                    const isInvoiced = getEstimateIsInvoiced(item);

                    return (
                      <tr key={estKey} className="hover:bg-gray-50/60 transition-colors align-middle">
                        {/* 1. Actions */}
                        <td className="py-2.5 px-3 align-middle">
                          <div className="flex items-center gap-1.5">
                            <button type="button" onClick={() => history(`/qb-desktop/estimates/detail/${estKey}`)} title="View Details"
                              className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                            {!isInvoiced && (
                              <button
                                type="button"
                                onClick={() => handleConvertToInvoice(item)}
                                title="Convert to Invoice"
                                className="size-7 rounded-lg bg-[#115B9D]/10 hover:bg-[#115B9D]/20 flex items-center justify-center text-[#115B9D] transition-colors"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        {/* 2. Doc # */}
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D] truncate max-w-full">
                            {docNum !== "—" ? `#${docNum}` : "—"}
                          </span>
                        </td>
                        {/* 3. Customer */}
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="size-7 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                              <span className="text-[9px] font-semibold text-[#115B9D]">{getInitials(custName)}</span>
                            </div>
                            <p className="text-xs font-medium text-gray-800 truncate">{custName}</p>
                          </div>
                        </td>
                        {/* 4. Txn Date */}
                        <td className="py-2.5 px-3 align-middle text-xs text-gray-600">{formatDate(estDate)}</td>
                        {/* 5. Status */}
                        <td className="py-2.5 px-3 align-middle">
                          <StatusBadge status={statusText} />
                        </td>
                        {/* 6. Source */}
                        <td className="py-2.5 px-3 align-middle">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#115B9D]/8 border border-[#115B9D]/15 text-[10px] font-semibold text-[#115B9D] capitalize">
                            {sourceText}
                          </span>
                        </td>
                        {/* 7. Total Tax */}
                        <td className="py-2.5 px-3 align-middle text-xs text-gray-700 font-medium">
                          {formatCurrency(totalTax)}
                        </td>
                        {/* 8. Email */}
                        <td className="py-2.5 px-3 align-middle text-xs text-gray-600 truncate max-w-[140px]" title={email}>
                          {email}
                        </td>
                        {/* 9. Total Amount */}
                        <td className="py-2.5 px-3 align-middle">
                          <p className="text-xs font-semibold text-gray-800 whitespace-nowrap">{formatCurrency(amount)}</p>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 mt-3">
            {filteredEstimates.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <p className="text-sm text-gray-500">No estimates found</p>
              </div>
            ) : (
              filteredEstimates.map((item, index) => {
                const estKey = getEstimateKey(item) || index;
                const docNum = getEstimateDocNumber(item);
                const custName = getEstimateCustomerName(item);
                const estDate = getEstimateDate(item);
                const amount = getEstimateTotal(item);
                const statusText = getEstimateStatus(item);
                const totalTax = getEstimateTotalTax(item);
                const email = getEstimateEmail(item);
                const sourceText = getEstimateSource(item);
                const isInvoiced = getEstimateIsInvoiced(item);

                return (
                  <div key={estKey} className="border border-gray-200 rounded-xl p-4 bg-white">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D]">
                            {docNum !== "—" ? `#${docNum}` : "—"}
                          </span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#115B9D]/8 border border-[#115B9D]/15 text-[9px] font-semibold text-[#115B9D] capitalize">
                            {sourceText}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-1">{custName}</p>
                        {email !== "—" && <p className="text-xs text-gray-500 mt-0.5">{email}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={statusText} />
                        <button type="button" onClick={() => history(`/qb-desktop/estimates/detail/${estKey}`)} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Txn Date</span>
                      <p className="text-xs text-gray-700">{formatDate(estDate)}</p>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Total Tax</span>
                      <p className="text-xs font-medium text-gray-700">{formatCurrency(totalTax)}</p>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Total Amount</span>
                      <p className="text-sm font-semibold text-gray-800">{formatCurrency(amount)}</p>
                    </div>
                    {!isInvoiced && (
                      <div className="pt-3 mt-1 border-t border-gray-100">
                        <button type="button" onClick={() => handleConvertToInvoice(item)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#115B9D] hover:opacity-80 text-white text-sm font-medium transition-all">
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                          </svg>
                          Convert to Invoice
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Estimate {getEstimateDocNumber(selectedEstimate) !== "—" ? `#${getEstimateDocNumber(selectedEstimate)}` : "Details"}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">{getEstimateCustomerName(selectedEstimate)}</p>
                    </div>
                    <button type="button" onClick={closeModal} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                    </button>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-5">
                    {[
                      { label: "TxnID / ID", value: getEstimateKey(selectedEstimate) },
                      { label: "Date", value: formatDate(getEstimateDate(selectedEstimate)) },
                      { label: "Subtotal", value: formatCurrency(getEstimateTotal(selectedEstimate)) },
                      { label: "Status", value: getEstimateStatus(selectedEstimate) },
                    ].map(({ label, value }) =>
                      value ? (
                        <div key={label} className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0">
                          <p className="text-xs text-gray-500 font-medium">{label}</p>
                          <p className="text-sm text-gray-800 font-medium">{value}</p>
                        </div>
                      ) : null
                    )}
                  </div>

                  {/* Line Items */}
                  {getEstimateLines(selectedEstimate).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Line Items</h3>
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600">Item</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600">Qty</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600">Rate</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {getEstimateLines(selectedEstimate).map((l, i) => {
                              const itemName = l.name || l.itemName || l.ItemRef?.FullName || l.raw?.ItemRef?.FullName || l.description || l.Desc || l.Description || "—";
                              const qty = l.quantity || l.Quantity || l.Qty || 1;
                              const rate = l.rate || l.Rate || l.UnitPrice || 0;
                              const amount = l.amount || l.Amount || (Number(qty) * Number(rate));
                              return (
                                <tr key={i} className="hover:bg-gray-50/50">
                                  <td className="px-3 py-2 text-gray-700">{itemName}</td>
                                  <td className="px-3 py-2 text-right text-gray-700">{qty}</td>
                                  <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(rate)}</td>
                                  <td className="px-3 py-2 text-right font-semibold text-gray-800">{formatCurrency(amount)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Convert button inside modal */}
                  {!getEstimateIsInvoiced(selectedEstimate) && (
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={() => { closeModal(); handleConvertToInvoice(selectedEstimate); }}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#115B9D] hover:opacity-80 text-white text-sm font-medium transition-all"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                        </svg>
                        Convert to Invoice
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}

